/**
 * Client Drill-Down Route
 *
 * Detailed view for a specific client showing:
 * - Combined faturamento + custos analysis
 * - Dynamic dimension tabs (following DRE pattern):
 *   - Por Job (dimension grouping)
 *   - Por Tipo (Receita vs Custo)
 *   - Por Fornecedor (dimension grouping)
 *   - Transacoes (raw transactions)
 * - Uses GenericDrillDown patterns for filtering and comparison
 *
 * Route: /_auth/financial/client-matrix/$clientId
 *
 * @see docs/research/trpc/frontend/src/routes/client-matrix/drilldown.$clientId.tsx - Reference implementation
 */

import { trpc } from '@/api/trpc';
import { GenericDrillDown } from '@/app/financial/dre/drill-down/GenericDrillDown';
import type {
  ChartType,
  ComparisonDates,
  DimensionTab,
  DrillDownGranularity,
  PeriodDates,
} from '@/app/financial/dre/drill-down/types';
import { FinancialSkeleton, FinancialEmptyState } from '@/app/financial/shared/components';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

// ============================================================================
// CONSTANTS
// ============================================================================

// Valid granularity values
const VALID_GRANULARITIES: DrillDownGranularity[] = ['accumulated', 'yearly', 'quarterly', 'monthly'];

// Valid chart types
const VALID_CHART_TYPES: ChartType[] = ['bar', 'line'];

// ============================================================================
// TYPES
// ============================================================================

// Unified row for GenericDrillDown - uses index signature for compatibility
interface UnifiedRow {
  [key: string]: string | number | null | undefined;
  Data: string | null;
  Documento: string | null;
  Valor: number | null;
  Tipo: string;
  // Common fields
  JOB?: string | null;
  // Faturamento fields
  Cliente?: string | null;
  ReceitaBruta?: number | null;
  Encaminhadora?: number | null;
  // Custos fields
  Fornecedor?: string | null;
  Natureza?: string | null;
  PIT?: string | null;
}

// ============================================================================
// SEARCH PARAMS VALIDATION
// ============================================================================

interface DrillDownSearchParams {
  startDate: string;
  endDate: string;
  compareStartDate?: string;
  compareEndDate?: string;
  granularity?: DrillDownGranularity;
  chartType?: ChartType;
  selectedDimensions?: string[];
}

// ============================================================================
// ROUTE DEFINITION
// ============================================================================

export const Route = createFileRoute('/_auth/financial/client-matrix/$clientId')({
  validateSearch: (search: Record<string, unknown>): DrillDownSearchParams => ({
    startDate: (search.startDate as string) || '',
    endDate: (search.endDate as string) || '',
    compareStartDate: (search.compareStartDate as string) || undefined,
    compareEndDate: (search.compareEndDate as string) || undefined,
    granularity: VALID_GRANULARITIES.includes(search.granularity as DrillDownGranularity)
      ? (search.granularity as DrillDownGranularity)
      : undefined,
    chartType: VALID_CHART_TYPES.includes(search.chartType as ChartType)
      ? (search.chartType as ChartType)
      : undefined,
    selectedDimensions: Array.isArray(search.selectedDimensions)
      ? (search.selectedDimensions as string[])
      : typeof search.selectedDimensions === 'string'
        ? [search.selectedDimensions]
        : undefined,
  }),
  component: ClientDrillDownComponent,
});

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function toNum(val: unknown): number {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  if (typeof val === 'object' && val !== null && 'toNumber' in val) {
    return (val as { toNumber: () => number }).toNumber();
  }
  const n = Number(val);
  return isNaN(n) ? 0 : n;
}

// Parse date string as local time
function parseLocalDate(dateStr: string): Date {
  return new Date(dateStr + 'T00:00:00');
}

// Format date for display
function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = parseLocalDate(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR');
}

// Format date from Date object or string
function formatDateValue(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value.split('T')[0];
  return value.toISOString().split('T')[0];
}

// ============================================================================
// LOADING SKELETON (Uses Design System)
// ============================================================================

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="p-8 bg-brand-dark min-h-screen">
      <FinancialSkeleton variant="full-dashboard" />
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function ClientDrillDownComponent(): JSX.Element {
  const { clientId } = Route.useParams();
  const {
    startDate,
    endDate,
    compareStartDate,
    compareEndDate,
    granularity,
    chartType,
    selectedDimensions,
  } = Route.useSearch();
  const navigate = useNavigate();

  // State
  const [currentGranularity, setCurrentGranularity] = useState<DrillDownGranularity>(
    granularity || 'monthly'
  );
  const [currentChartType, setCurrentChartType] = useState<ChartType>(
    chartType || 'bar'
  );
  const [currentSelectedDimensions, setCurrentSelectedDimensions] = useState<string[]>(
    selectedDimensions || []
  );
  const [comparisonDates, setComparisonDates] = useState<ComparisonDates | null>(
    compareStartDate && compareEndDate
      ? { startDate, endDate, compareStartDate, compareEndDate }
      : null
  );

  // Effective dates
  const effectiveStartDate = comparisonDates?.startDate || startDate;
  const effectiveEndDate = comparisonDates?.endDate || endDate;
  const effectiveCompStartDate = comparisonDates?.compareStartDate || '';
  const effectiveCompEndDate = comparisonDates?.compareEndDate || '';

  // Fetch client detail (faturamento + custos in one call)
  const {
    data: clientDetailResult,
    isLoading,
    error,
    refetch,
  } = trpc.dre.getClientDetail.useQuery(
    {
      dataInicial: new Date(effectiveStartDate),
      dataFinal: new Date(effectiveEndDate),
      codigoCliente: clientId,
    },
    {
      enabled: !!effectiveStartDate && !!effectiveEndDate,
      retry: false,
    }
  );

  // Fetch comparison client detail
  const { data: compClientDetailResult } = trpc.dre.getClientDetail.useQuery(
    {
      dataInicial: effectiveCompStartDate ? new Date(effectiveCompStartDate) : new Date(),
      dataFinal: effectiveCompEndDate ? new Date(effectiveCompEndDate) : new Date(),
      codigoCliente: clientId,
    },
    {
      enabled: !!effectiveCompStartDate && !!effectiveCompEndDate,
      retry: false,
    }
  );

  // Get client name from response
  const clientName = useMemo(() => {
    return clientDetailResult?.client?.nomeCliente || clientId;
  }, [clientDetailResult, clientId]);

  // Transform and combine all data
  const { data, comparisonData, dimensionTabs } = useMemo(() => {
    const faturamentoData = clientDetailResult?.faturamento || [];
    const custosData = clientDetailResult?.custos || [];
    const compFaturamentoData = compClientDetailResult?.faturamento || [];
    const compCustosData = compClientDetailResult?.custos || [];

    // Transform faturamento (receitas) - positive values
    const receitaRows: UnifiedRow[] = faturamentoData.map(r => ({
      Data: r.data ?? null,
      Documento: r.nf ?? null,
      Valor: toNum(r.receitaBruta),
      Tipo: 'Receita',
      JOB: r.job || 'Sem Job',
      Cliente: clientDetailResult?.client?.nomeCliente || null,
      Fornecedor: null,
    }));

    // Transform custos - positive values (costs are positive in their own context)
    const custoRows: UnifiedRow[] = custosData.map(r => ({
      Data: r.data ?? null,
      Documento: r.numeroTitulo ?? null,
      Valor: toNum(r.valor),
      Tipo: 'Custo',
      JOB: r.job || 'Sem Job',
      Cliente: null,
      Fornecedor: r.fornecedor || 'Sem Fornecedor',
      Natureza: r.natureza ?? null,
      PIT: r.pit ?? null,
    }));

    // Combine all data
    const allRows = [...receitaRows, ...custoRows].sort((a, b) => {
      if (!a.Data || !b.Data) return 0;
      return new Date(b.Data).getTime() - new Date(a.Data).getTime();
    });

    // Comparison data
    let compRows: UnifiedRow[] | null = null;
    if (compFaturamentoData.length > 0 || compCustosData.length > 0) {
      const compReceitaRows: UnifiedRow[] = compFaturamentoData.map(r => ({
        Data: r.data ?? null,
        Documento: r.nf ?? null,
        Valor: toNum(r.receitaBruta),
        Tipo: 'Receita',
        JOB: r.job || 'Sem Job',
        Cliente: compClientDetailResult?.client?.nomeCliente || null,
        Fornecedor: null,
      }));
      const compCustoRows: UnifiedRow[] = compCustosData.map(r => ({
        Data: r.data ?? null,
        Documento: r.numeroTitulo ?? null,
        Valor: toNum(r.valor),
        Tipo: 'Custo',
        JOB: r.job || 'Sem Job',
        Cliente: null,
        Fornecedor: r.fornecedor || 'Sem Fornecedor',
        Natureza: r.natureza ?? null,
        PIT: r.pit ?? null,
      }));
      compRows = [...compReceitaRows, ...compCustoRows].sort((a, b) => {
        if (!a.Data || !b.Data) return 0;
        return new Date(b.Data).getTime() - new Date(a.Data).getTime();
      });
    }

    // Build dimension tabs
    const tabs: DimensionTab[] = [];

    // Por Job - groups by job showing combined margin (receitas - custos)
    const hasJobs = allRows.some(r => r.JOB && r.JOB !== 'Sem Job');
    if (hasJobs) {
      tabs.push({ id: 'jobs', label: 'Por Job', key: 'JOB', icon: '' });
    }

    // Por Tipo - groups into Receita vs Custo buckets
    tabs.push({ id: 'tipo', label: 'Por Tipo', key: 'Tipo', icon: '' });

    // Por Fornecedor - groups custos by supplier
    const hasSuppliers = custoRows.some(r => r.Fornecedor && r.Fornecedor !== 'Sem Fornecedor');
    if (hasSuppliers) {
      tabs.push({ id: 'fornecedores', label: 'Por Fornecedor', key: 'Fornecedor', icon: '' });
    }

    // Transactions tab - shows all raw transactions
    tabs.push({ id: 'transactions', label: 'Transacoes', key: null, icon: '' });

    return { data: allRows, comparisonData: compRows, dimensionTabs: tabs };
  }, [clientDetailResult, compClientDetailResult]);

  const subtitle = `Periodo: ${formatDate(effectiveStartDate)} a ${formatDate(effectiveEndDate)}`;

  // Navigation handlers
  const handleBack = useCallback(() => {
    void navigate({
      to: '/financial/client-matrix',
      search: { startDate, endDate, granularity: currentGranularity } as Record<string, string>,
    });
  }, [navigate, startDate, endDate, currentGranularity]);

  const handleComparisonChange = useCallback((dates: ComparisonDates | null) => {
    setComparisonDates(dates);
    void navigate({
      to: '/financial/client-matrix/$clientId',
      params: { clientId },
      search: {
        startDate: dates?.startDate || startDate,
        endDate: dates?.endDate || endDate,
        compareStartDate: dates?.compareStartDate || undefined,
        compareEndDate: dates?.compareEndDate || undefined,
        granularity: currentGranularity,
      },
    });
  }, [clientId, startDate, endDate, currentGranularity, navigate]);

  const handlePeriodChange = useCallback((dates: PeriodDates) => {
    setComparisonDates(null);
    void navigate({
      to: '/financial/client-matrix/$clientId',
      params: { clientId },
      search: {
        startDate: dates.startDate,
        endDate: dates.endDate,
        granularity: currentGranularity,
      },
    });
  }, [clientId, currentGranularity, navigate]);

  const handleGranularityChange = useCallback((newGranularity: DrillDownGranularity) => {
    setCurrentGranularity(newGranularity);
    void navigate({
      to: '/financial/client-matrix/$clientId',
      params: { clientId },
      search: {
        startDate,
        endDate,
        compareStartDate: comparisonDates?.compareStartDate || undefined,
        compareEndDate: comparisonDates?.compareEndDate || undefined,
        granularity: newGranularity,
        chartType: currentChartType,
        selectedDimensions: currentSelectedDimensions.length > 0 ? currentSelectedDimensions : undefined,
      },
    });
  }, [clientId, startDate, endDate, comparisonDates, currentChartType, currentSelectedDimensions, navigate]);

  const handleChartTypeChange = useCallback((newChartType: ChartType) => {
    setCurrentChartType(newChartType);
  }, []);

  const handleDimensionToggle = useCallback((dimensionKey: string) => {
    const newDimensions = currentSelectedDimensions.includes(dimensionKey)
      ? currentSelectedDimensions.filter(d => d !== dimensionKey)
      : [...currentSelectedDimensions, dimensionKey];
    setCurrentSelectedDimensions(newDimensions);
  }, [currentSelectedDimensions]);

  const handleClearDimensions = useCallback(() => {
    setCurrentSelectedDimensions([]);
  }, []);

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="p-8 bg-brand-dark min-h-screen">
        <FinancialEmptyState
          variant="error"
          title="Erro ao carregar dados"
          description={error.message}
          actionLabel="Tentar Novamente"
          onAction={() => { void refetch(); }}
          secondaryActionLabel="Voltar à Matriz"
          onSecondaryAction={handleBack}
        />
      </div>
    );
  }

  // No data state
  if (data.length === 0) {
    return (
      <div className="p-8 bg-brand-dark min-h-screen">
        <h2 className="text-2xl font-display font-bold text-white mb-6">{clientName}</h2>
        <FinancialEmptyState
          variant="no-data"
          title="Sem dados disponíveis"
          description="Nenhum dado encontrado para o período selecionado."
          actionLabel="Voltar à Matriz"
          onAction={handleBack}
        />
      </div>
    );
  }

  return (
    <GenericDrillDown
      data={data}
      title={clientName}
      subtitle={subtitle}
      comparisonData={comparisonData}
      onBack={handleBack}
      backLabel="Voltar a Matriz"
      onComparisonChange={handleComparisonChange}
      onPeriodChange={handlePeriodChange}
      startDate={parseLocalDate(effectiveStartDate)}
      endDate={parseLocalDate(effectiveEndDate)}
      comparisonStartDate={effectiveCompStartDate ? parseLocalDate(effectiveCompStartDate) : undefined}
      comparisonEndDate={effectiveCompEndDate ? parseLocalDate(effectiveCompEndDate) : undefined}
      dimensionTabs={dimensionTabs}
      granularity={currentGranularity}
      onGranularityChange={handleGranularityChange}
      chartType={currentChartType}
      onChartTypeChange={handleChartTypeChange}
      selectedDimensions={currentSelectedDimensions}
      onDimensionToggle={handleDimensionToggle}
      onClearDimensions={handleClearDimensions}
    />
  );
}

export default ClientDrillDownComponent;
